// Simple Cache Utility for API responses
// Stores data in memory with expiration

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresIn: number;
}

class SimpleCache {
  private cache: Map<string, CacheEntry<any>> = new Map();

  set<T>(key: string, data: T, expiresInMs: number = 300000): void {
    // Default: 5 minutes
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresIn: expiresInMs
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if expired
    if (age > entry.expiresIn) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const age = now - entry.timestamp;

    if (age > entry.expiresIn) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  // Clear expired entries
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age > entry.expiresIn) {
        this.cache.delete(key);
      }
    }
  }
}

// Create singleton instance
export const cache = new SimpleCache();

// Auto cleanup every 5 minutes
setInterval(() => {
  cache.cleanup();
}, 300000);

// Helper function for caching API calls
export async function cachedApiCall<T>(
  key: string,
  fetcher: () => Promise<T>,
  expiresInMs: number = 300000
): Promise<T> {
  // Check cache first
  const cached = cache.get<T>(key);
  if (cached !== null) {
    console.log(`📦 Cache hit: ${key}`);
    return cached;
  }

  // Fetch fresh data
  console.log(`🌐 Cache miss: ${key} - Fetching...`);
  const data = await fetcher();
  
  // Store in cache
  cache.set(key, data, expiresInMs);
  
  return data;
}
